import React, { FC, memo, useCallback, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { AnalyticsDataMediaType } from 'src/types/googleAnalyticsEvents';
import { useEventLearnableSnippet } from 'src/analytics/useEventLearnableSnippet';
import { useAudioPlayback } from 'src/context/AudioContext';
import { useKeyboardShortcut } from 'src/hooks';
import { defineMessages, useIntl } from 'react-intl';
import * as S from './styles';

const msgs = defineMessages({
  audio: {
    id: 'learning_session_learnables_card_audio',
  },
});

type WrappedAudioPlayerProps = {
  src: string;
  autoPlay?: boolean;
  playOnKeyboardShortcut?: boolean;
  larger?: boolean;
  className?: string;
  ariaText?: string;
  onClick?: () => void;
  title: string;
};

export const AudioPlayer: FC<WrappedAudioPlayerProps> = ({
  src,
  autoPlay = false,
  playOnKeyboardShortcut = false,
  larger = false,
  ariaText,
  onClick,
  title,
  ...restProps
}) => {
  const { playing, play, stopAll } = useAudioPlayback(src);
  const intl = useIntl();

  const eventLearnableSnippet = useEventLearnableSnippet(AnalyticsDataMediaType.audio, {
    title,
    url: src,
  });

  const trackedPlay = useCallback(async () => {
    window.dataLayer.push(eventLearnableSnippet);
    await play();
  }, [eventLearnableSnippet, play]);

  const togglePlay = useCallback(async () => {
    if (playing) {
      stopAll();
    } else {
      await trackedPlay();
    }
    if (onClick) onClick();
  }, [onClick, trackedPlay, playing, stopAll]);

  useEffect(() => {
    if (autoPlay) void trackedPlay();
  }, [autoPlay, trackedPlay]);

  useKeyboardShortcut('r', playOnKeyboardShortcut ? trackedPlay : undefined);

  const props = { ...restProps, onClick: togglePlay, playing };

  return larger ? (
    <LargeAudioPlayer ariaText={intl.formatMessage(msgs.audio)} {...props} />
  ) : (
    <DefaultAudioPlayer ariaText={intl.formatMessage(msgs.audio)} {...props} />
  );
};

type AudioPlayerProps = {
  onClick: () => void;
  playing: boolean;
  className?: string;
  ariaText?: string;
};

export const LargeAudioPlayer: FC<AudioPlayerProps> = memo(function LargeAudioPlayer({
  onClick,
  playing,
  className,
  ariaText,
  ...props
}) {
  return (
    <S.ButtonBackground {...props}>
      <S.PulseBackground>
        <S.Button
          aria-label={ariaText}
          title={ariaText}
          className={className}
          onClick={onClick}
          data-testid="audioPlayer"
          $larger
        >
          <AnimatePresence>{playing && <S.PulseLarger data-testid="pulse" />}</AnimatePresence>
          <S.SpeakerIcon $larger />
        </S.Button>
      </S.PulseBackground>
    </S.ButtonBackground>
  );
});

export const DefaultAudioPlayer: FC<AudioPlayerProps> = memo(function DefaultAudioPlayer({
  onClick,
  playing,
  ariaText,
  ...props
}) {
  return (
    <S.Button
      aria-label={ariaText}
      title={ariaText}
      onClick={onClick}
      data-testid="audioPlayer"
      {...props}
    >
      <AnimatePresence>{playing && <S.Pulse data-testid="pulse" />}</AnimatePresence>
      <S.SpeakerIcon />
    </S.Button>
  );
});
